import React from 'react';
import { Box, Collapse, HStack, Icon, IconButton, Text } from '@chakra-ui/react';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';

export interface InvoiceDetailFoldableSectionProps {
    title: string;
    isOpen: boolean;
    onToggle: (next: boolean) => void;
    children: React.ReactNode;
    /** Shown immediately after the title (e.g. help icon); not part of the section toggle button. */
    titleAccessory?: React.ReactNode;
    /** Optional actions (e.g. buttons) shown on the right; clicks do not toggle the section. */
    headerRight?: React.ReactNode;
    /** Padding around expanded content area. */
    contentPadding?: number | string;
    /** Allow children to extend beyond section bounds (e.g. row drag handles). */
    allowOverflow?: boolean;
    /** Stable id on the section wrapper for scroll-into-view (e.g. reconciliation banner). */
    sectionId?: string;
}

export function InvoiceDetailFoldableSection({
    title,
    isOpen,
    onToggle,
    children,
    titleAccessory,
    headerRight,
    contentPadding = 4,
    allowOverflow = false,
    sectionId,
}: InvoiceDetailFoldableSectionProps) {
    const panelId = React.useId();
    return (
        <Box
            id={sectionId}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
            overflow={allowOverflow ? 'visible' : 'hidden'}
        >
            <HStack px={4} py={2} w="100%" spacing={2} align="center">
                <HStack spacing={1} align="center" flexShrink={0} minW={0}>
                    <Text color="gray.900" fontSize="sm" fontWeight="bold" textAlign="left" whiteSpace="nowrap">
                        {title}
                    </Text>
                    {titleAccessory != null ? (
                        <Box flexShrink={0} display="flex" alignItems="center">
                            {titleAccessory}
                        </Box>
                    ) : null}
                </HStack>
                <Box flex="1" minW={0} aria-hidden />
                {headerRight != null ? (
                    <Box flexShrink={0} onClick={(e) => e.stopPropagation()}>
                        {headerRight}
                    </Box>
                ) : null}
                <IconButton
                    aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
                    icon={<Icon as={(isOpen ? TbChevronUp : TbChevronDown) as React.ElementType} boxSize={4} />}
                    size="sm"
                    variant="ghost"
                    color="gray.600"
                    onClick={() => onToggle(!isOpen)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                />
            </HStack>
            <Collapse
                in={isOpen}
                animateOpacity
                transition={{ enter: { duration: 0.35 }, exit: { duration: 0.25 } }}
                style={allowOverflow ? { overflow: 'visible' } : undefined}
            >
                <Box
                    id={panelId}
                    borderTop="1px solid"
                    borderColor="gray.200"
                    p={contentPadding}
                    overflow={allowOverflow ? 'visible' : 'hidden'}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}
